import type { LocationQuery } from '#vue-router'

import { getFirstQueryValue } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'

export const LOGIN_REDIRECT_QUERY_KEY = 'redirect_url'

const DEFAULT_POST_LOGIN_PATH = AppRoute.Homepage

interface LoginLocation {
  path: AppRoute.Login
  query?: Record<typeof LOGIN_REDIRECT_QUERY_KEY, string>
}

/**
 * Resolving against a throwaway origin lets the URL parser decide what is same-origin, so
 * `//host`, `/\host` and `scheme:` tricks are caught the way a browser would read them.
 */
const PARSE_ORIGIN = 'http://admin.invalid'

/** Vue Router matches paths case-insensitively and ignores a trailing slash, so `/LOGIN/` is `/login`. */
const toRouteMatchKey = (pathname: string): string =>
  pathname.toLowerCase().replace(/\/+$/, '') || AppRoute.Homepage

const toRedirectTarget = (raw: string | null): string | null => {
  if (!raw?.startsWith('/') || !URL.canParse(raw, PARSE_ORIGIN)) return null

  const url = new URL(raw, PARSE_ORIGIN)
  const routeKey = toRouteMatchKey(url.pathname)
  const isSameOrigin = url.origin === PARSE_ORIGIN
  const isLoopback = routeKey === AppRoute.Homepage || routeKey === AppRoute.Login

  return isSameOrigin && !isLoopback ? `${url.pathname}${url.search}${url.hash}` : null
}

export const buildLoginLocation = (fullPath: string): LoginLocation => {
  const target = toRedirectTarget(fullPath)

  return target
    ? { path: AppRoute.Login, query: { [LOGIN_REDIRECT_QUERY_KEY]: target } }
    : { path: AppRoute.Login }
}

export const resolvePostLoginPath = (query: LocationQuery): string =>
  toRedirectTarget(getFirstQueryValue(query[LOGIN_REDIRECT_QUERY_KEY])) ?? DEFAULT_POST_LOGIN_PATH
